var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeBreakpointObserver } from '@inter-ikea-kompis/themes';
import { ProductDescriptionProductMeasureEnum } from '@inter-ikea-kompis/utilities';
import { AddToBagLayoutEnum, AddToBagStateEnum } from '@inter-ikea-kompis/component-add-to-bag';
import { AddToBag } from '@inter-ikea-kompis/component-add-to-bag';
import { CarouselItem, CarouselLayoutEnum } from '@inter-ikea-kompis/component-carousel';
import { Carousel } from '@inter-ikea-kompis/component-carousel';
import { ProductCardSelectBehaviourEnum } from '@inter-ikea-kompis/component-product-card';
import { ProductCard } from '@inter-ikea-kompis/component-product-card';
import ProductShelfProductCardMediaEnum from '../../enums/ProductShelfProductCardMediaEnum.js';
import ProductShelfProductLinkEnum from '../../enums/ProductShelfProductLinkEnum.js';
import ProductShelfStyle from './ProductShelfStyle.js';
const ADD_TO_BAG_LAYOUT = AddToBagLayoutEnum.icon;
/**
 * ProductShelf View component.
 *
 * @event addToCart Emitted when user clicks on the add to cart button.
 * @event addToList Emitted when user clicks on the add to shopping list button.
 * @event change Emits when selectedIndex has changed. e.g. when user is clicking on next-button
 * @event linkClick Emitted when user clicks on PIP link.
 * @event modalClose Triggered when the modal has been closed
 * @event modalOpen Triggered when a modal has been opened. event.detail.type tells you the type
 */
export default class ProductShelf extends KompisElement {
    constructor() {
        super(...arguments);
        // Set which slide index that should be active
        this.selectedIndex = 0;
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // A list of IDs
        this.itemsAddedToCart = null;
        // A list of IDs
        this.itemsAddedToList = null;
        // A list of IDs
        this.loadingItemsAddedToCart = null;
        // A list of IDs
        this.loadingItemsAddedToList = null;
        // Sets how media should be displayed.
        this.productCardMedia = ProductShelfProductCardMediaEnum.singleImage;
        // Visible modal.
        this.visibleModal = null;
        // Hides the add to bag component. This attribute should be set to "true" when in a Kiosk
        this.hideAddToBag = false;
        // Sets the link behaviour. This attribute should be set to "disabled" when in a Kiosk.
        this.productLink = ProductShelfProductLinkEnum.productInformationPage;
        // Show price excl tax.
        this.showPriceExclTax = false;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Skip listing label
        this.skipListingLabel = null;
        // Show design text for Product Cards in Shelf.
        this.showDesignText = false;
        // Override market setting "useMetricMeasures" by controlling the measurement system in the planner.
        this.measurementSystem = null;
        // Show product measures for Product Cards in Shelf.
        this.productMeasures = ProductDescriptionProductMeasureEnum.none;
        this.breakpointObserver = new ThemeBreakpointObserver(() => this.requestUpdate());
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${ProductShelfStyle.getStyle({ theme: this.theme })}
			</style>

			<div class="container">
				<kompis-carousel
					@change="${this.onChange.bind(this)}"
					.theme="${this.theme}"
					.layout="${CarouselLayoutEnum.default}"
					.selectedIndex="${this.selectedIndex}"
					.showScrollbar="${true}"
					.showArrows="${true}"
					.paddingBottom="${true}"
					.slideVisibleItems="${true}"
					.skipListingLabel="${this.skipListingLabel}"
				>
					${this.getCarouselSlides()}
				</kompis-carousel>
			</div>
		`;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.breakpointObserver.disconnect();
    }
    connectedCallback() {
        this.customElements.define('kompis-carousel', Carousel);
        this.customElements.define('kompis-carousel-item', CarouselItem);
        this.customElements.define('kompis-add-to-bag', AddToBag);
        this.customElements.define('kompis-product-card', ProductCard);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('theme')) {
            this.breakpointObserver.observe(this.theme);
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return !!this.localizedInformation || !!(this.translations && this.settings);
    }
    onChange(event) {
        this.dispatchEvent(new CustomEvent('change', {
            bubbles: true,
            detail: { selectedIndex: event.detail.selectedIndex, type: 'carousel' }
        }));
    }
    /**
     * Get swiper slides with ProductShelf Items.
     *
     * @returns Template.
     */
    getCarouselSlides() {
        return this.products.map(this.getCarouselItem.bind(this));
    }
    getCarouselItem(product) {
        return html `
			<kompis-carousel-item>
				${this.getProductCard(product)} ${this.getAddToBag(product)}
			</kompis-carousel-item>
		`;
    }
    getProductCard(product) {
        const productCardMedia = this.productCardMedia === ProductShelfProductCardMediaEnum.switchImageOnHover
            ? ProductShelfProductCardMediaEnum.switchImageOnHover
            : ProductShelfProductCardMediaEnum.singleImage;
        const isModalVisible = product.content?.ruItemNo === this.visibleModal?.itemId ? this.visibleModal?.modal : null;
        return html `<kompis-product-card
			.product="${product}"
			.visibleModal="${isModalVisible}"
			.theme="${this.theme}"
			.showPriceExclTax="${this.showPriceExclTax}"
			.media="${productCardMedia}"
			.disableLinks="${this.disableLinks}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.selectBehaviour="${this.getProductCardSelectBehaviour()}"
			.productMeasures="${this.productMeasures}"
			.showDesignText="${this.showDesignText}"
			.measurementSystem="${this.measurementSystem}"
			@select="${() => this.dispatchEvent(new CustomEvent('linkClick', { bubbles: true, detail: product }))}"
			@modalClose="${() => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: { visibleModal: null } }))}"
			@modalOpen="${(event) => this.dispatchEvent(new CustomEvent('modalOpen', {
            bubbles: true,
            detail: {
                visibleModal: {
                    modal: event.detail.visibleModal,
                    itemNo: product.content?.ruItemNo
                }
            }
        }))}"
		>
		</kompis-product-card>`;
    }
    getAddToBag(product) {
        if (this.hideAddToBag) {
            return null;
        }
        return html `<kompis-add-to-bag
			.layout="${ADD_TO_BAG_LAYOUT}"
			.localizedInformation="${this.localizedInformation}"
			.translations="${this.translations}"
			.settings="${this.settings}"
			.theme="${this.theme}"
			.shoppingProducts="${[this.getShoppingProduct(product)]}"
			.addToCartState="${this.getAddToBagState(product, this.loadingItemsAddedToCart, this.itemsAddedToCart)}"
			.addToListState="${this.getAddToBagState(product, this.loadingItemsAddedToList, this.itemsAddedToList)}"
			@addToCart="${(event) => this.dispatchEvent(new CustomEvent('addToCart', {
            bubbles: true,
            detail: { shoppingItems: event.detail.shoppingItems }
        }))}"
			@addToList="${(event) => this.dispatchEvent(new CustomEvent('addToList', {
            bubbles: true,
            detail: { shoppingItems: event.detail.shoppingItems }
        }))}"
		>
		</kompis-add-to-bag>`;
    }
    getAddToBagState(product, loadingItems, itemsAdded) {
        if (!product.content?.ruItemNo) {
            return AddToBagStateEnum.default;
        }
        if (loadingItems && loadingItems.includes(product.content.ruItemNo)) {
            return AddToBagStateEnum.loading;
        }
        if (itemsAdded && itemsAdded.includes(product.content.ruItemNo)) {
            return AddToBagStateEnum.confirmation;
        }
        return AddToBagStateEnum.default;
    }
    getShoppingProduct(product) {
        return {
            product: product,
            quantity: 1
        };
    }
    getProductCardSelectBehaviour() {
        switch (this.productLink) {
            case ProductShelfProductLinkEnum.disabled:
                return ProductCardSelectBehaviourEnum.disabled;
            case ProductShelfProductLinkEnum.eventOnly:
                return ProductCardSelectBehaviourEnum.selectable;
            case ProductShelfProductLinkEnum.productInformationPage:
                return ProductCardSelectBehaviourEnum.productInformationPageLink;
        }
    }
}
__decorate([
    property({ type: Object, required: true })
], ProductShelf.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], ProductShelf.prototype, "products", void 0);
__decorate([
    property({ type: Number, synchronous: true })
], ProductShelf.prototype, "selectedIndex", void 0);
__decorate([
    property({ type: Object })
], ProductShelf.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], ProductShelf.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], ProductShelf.prototype, "settings", void 0);
__decorate([
    property({ type: Object })
], ProductShelf.prototype, "itemsAddedToCart", void 0);
__decorate([
    property({ type: Object })
], ProductShelf.prototype, "itemsAddedToList", void 0);
__decorate([
    property({ type: Object })
], ProductShelf.prototype, "loadingItemsAddedToCart", void 0);
__decorate([
    property({ type: Object })
], ProductShelf.prototype, "loadingItemsAddedToList", void 0);
__decorate([
    property({ type: String })
], ProductShelf.prototype, "productCardMedia", void 0);
__decorate([
    property({ type: Object })
], ProductShelf.prototype, "visibleModal", void 0);
__decorate([
    property({ type: Boolean })
], ProductShelf.prototype, "hideAddToBag", void 0);
__decorate([
    property({ type: String })
], ProductShelf.prototype, "productLink", void 0);
__decorate([
    property({ type: Boolean })
], ProductShelf.prototype, "showPriceExclTax", void 0);
__decorate([
    property({ type: Boolean })
], ProductShelf.prototype, "disableLinks", void 0);
__decorate([
    property({ type: String })
], ProductShelf.prototype, "skipListingLabel", void 0);
__decorate([
    property({ type: Boolean })
], ProductShelf.prototype, "showDesignText", void 0);
__decorate([
    property({ type: String })
], ProductShelf.prototype, "measurementSystem", void 0);
__decorate([
    property({ type: String })
], ProductShelf.prototype, "productMeasures", void 0);
//# sourceMappingURL=ProductShelf.js.map